/**
 * Release notes shown in the "What's new" sheet after the user accepts an update.
 *
 * Releasing: bump `version` in package.json AND prepend an entry here with the
 * same version. The version is stamped into the bundle at build time.
 */

export interface Release {
  version: string
  date: string
  notes: string[]
}

export const APP_VERSION = __APP_VERSION__

/** Newest first. */
export const CHANGELOG: Release[] = [
  {
    version: '0.30.0',
    date: '2026-10-29',
    notes: [
      'A new account starts with a short look at what Matlist does, then asks what you always buy and where you shop, and puts it on the list in its aisles.',
      'A first tour points at the real buttons one at a time: open the list, add something, start shopping, invite the others. Skip it whenever you like.',
    ],
  },
  {
    version: '0.29.2',
    date: '2026-10-29',
    notes: [
      'Deleting a category asks first, and where its items should go. What you’ve bought before and your recipes move with them.',
      'Drag your categories into the order you like, and changes show on everyone’s phone at once.',
      'Two categories can’t share a name, and an item is never lost when someone deletes its category.',
    ],
  },
  {
    version: '0.29.1',
    date: '2026-10-28',
    notes: [
      'Matlist and the tabs stay at the top while you scroll, on every tab.',
      'A trip’s summary shows the cost you added, ready to change.',
    ],
  },
  {
    version: '0.29.0',
    date: '2026-10-27',
    notes: [
      'Someone forgot to finish their trip? End trip, next to Join, ends it for everyone. Matlist asks first and shows when it started and when they last picked something, so a trip still going isn’t ended by accident.',
      'Your Garmin can end a forgotten trip too: Menu › End trip on the start screen.',
    ],
  },
  {
    version: '0.28.4',
    date: '2026-10-27',
    notes: [
      'A trip with no cost yet shows Add cost on Home. One tap and the amount field is ready; it saves when you press Enter.',
      'Every trip, from Home, after shopping or in Stats, opens the same way, with Edit trip right there: the cost, the store with one tap, and the order you walked.',
    ],
  },
  {
    version: '0.28.2',
    date: '2026-10-27',
    notes: [
      'Finished a trip on your watch? Stats opens at your trips with that one ready for its cost. Every trip without a cost is marked there, with Add cost right in its row.',
    ],
  },
  {
    version: '0.28.1',
    date: '2026-10-27',
    notes: [
      'Recipes pasted from a photo of a page read their method again, even when the photo garbled a heading.',
      'Before saving an imported recipe, you can rename any ingredient, not only the ones Matlist matched to your list.',
      'Fixed: cream in a recipe was sometimes taken for cream sauce on your list.',
    ],
  },
  {
    version: '0.28.0',
    date: '2026-10-27',
    notes: [
      'Shopping goes one aisle at a time: everything from the same part of the store stays together, in the order you walk it. One item picked late no longer splits up the vegetables.',
      'Shopping together: whoever finishes the trip gets the summary and the cost question; everyone else still shopping gets a note with Keep shopping. The summary says who picked what.',
      'Stats lists every trip. Tap one to fix its store, its cost or the order you walked, or to delete it.',
      'Recipes import from many more sites, and from text copied off a magazine page. Norwegian, Danish, French, Italian and Spanish recipes work too. When a page can only be read in part, the editor opens with its text beside it.',
      'Signing up: your phone can suggest and save a strong password, and a mistyped address gets a “Did you mean …?”. Settings › Your data downloads everything your household has in Matlist as one file.',
      'On a computer or iPad: the list and the plan look like the phone’s, wider, with the sidebar always in view. Cooking mode shows every ingredient beside each step.',
      'New terms of use and privacy policy.',
    ],
  },
  {
    version: '0.27.0',
    date: '2026-10-21',
    notes: [
      'matlist.app has a new look: a short film of Matlist on the phone and the Garmin, and the four things it does best, each shown on the real app. Share it with someone who still keeps the list in the group chat.',
    ],
  },
  {
    version: '0.26.0',
    date: '2026-10-20',
    notes: [
      'Shopping together: when someone in your household is already shopping, the button says Join and their name. Tap it and you’re in the same trip, with one basket. Either of you can finish, and you both get the summary.',
      'Leaving a shared trip keeps it going for the others: close shopping mode and pick Leave trip.',
      'On your Garmin (Matlist 1.3 for the watch): the list follows the round edge of the watch, and the item to get sits in the middle between two lime lines. The menu and settings look the same. After you tick an item, Undo shows next to the BACK button. You can join someone’s trip from the watch too.',
      'Fixed: when two people shopped at the same time, finishing one trip cleared the other person’s basket.',
    ],
  },
  {
    version: '0.25.0',
    date: '2026-10-20',
    notes: [
      'Home shows your last trip: the store, how many items, the time in store, what it cost and how it compared with your usual trip. Tap it for the full summary.',
      'The Plan has the week’s days in a strip at the top. Tap a day to jump to it. An empty day takes one line, and the days gone by move to the bottom.',
      'Recipes sit two to a row with bigger covers, and each says what’s missing from your list.',
      'Fixed: searching in Add a meal no longer pushes the search out of sight when the keyboard opens. The same goes for every sheet you type in.',
      'Fixed: the list’s (i) note fits on the screen.',
    ],
  },
  {
    version: '0.24.1',
    date: '2026-10-20',
    notes: [
      'Behind the scenes: matlist.app shows the new look in its pictures. Nothing changes in the app.',
    ],
  },
  {
    version: '0.24.0',
    date: '2026-10-20',
    notes: [
      'A new Home page on your phone: a line about your household, what’s on the list with Start shopping, tonight’s dinner, your week, what’s running low, and a quick way to add a recipe.',
      'Swipe left and right to move between Home, List, Recipes and Plan. The header is calmer and the list starts higher.',
      'All items: scroll through everything you’ve bought before and add several at once, from the grid button next to New item.',
      'Cooking mode on a phone works sideways, with the ingredients beside each step. Tick one off when it’s in, and it folds away.',
      'Recipes: add one from the + button, and search from the magnifier. Quick add shows its best match right above the field.',
      'Fixed: the Delete button that appears when you swipe an item part of the way now works with a tap.',
    ],
  },
  {
    version: '0.23.0',
    date: '2026-10-19',
    notes: [
      'Free and Plus: every household starts with 30 days of everything. After that the shared list stays free, up to 15 open items, and Plus adds the rest: unlimited items, recipes, the meal plan, shopping on your watch and more.',
      'Importing a recipe fills in each ingredient’s name, amount and aisle itself, and asks only what it couldn’t settle. You can also paste a recipe’s text, and German recipes import properly.',
      'In cooking mode, every step shows the ingredients it uses, each with its own amount.',
      'On your Garmin (Matlist 1.2 for the watch): start shopping, pick the store, then tick items off a list that rolls like a wheel. When you finish, you get the same trip summary as on your phone.',
      'Fixed: ticked items no longer come back when someone adds to the list while you shop.',
    ],
  },
  {
    version: '0.22.2',
    date: '2026-10-14',
    notes: [
      'Behind the scenes: matlist.app moved to a new host. Nothing changes for you, and your list is where you left it.',
    ],
  },
  {
    version: '0.22.1',
    date: '2026-10-13',
    notes: [
      'New here? Get started, the ring beside Stats, walks you through the first steps: invite your household, add Matlist to your home screen, add your first items, save a recipe and pair your Garmin. It goes away when you’re done, and Settings brings it back.',
      'On iPhone, Matlist’s home-screen icon is bigger, like other apps’.',
    ],
  },
  {
    version: '0.22.0',
    date: '2026-10-13',
    notes: [
      'New here? Get started, the ring beside Stats, walks you through the first steps: invite your household, add Matlist to your home screen, add your first items, save a recipe and pair your Garmin. It goes away when you’re done, and Settings brings it back.',
      'On iPhone, Matlist’s home-screen icon is bigger, like other apps’.',
    ],
  },
  {
    version: '0.21.1',
    date: '2026-10-13',
    notes: [
      'A smaller release: matlist.app has a new look, and links to it now show a proper preview when you share them.',
    ],
  },
  {
    version: '0.21.0',
    date: '2026-10-12',
    notes: [
      'Import recipes from YouTube, TikTok and Instagram as well as recipe sites. Paste the link, and the photo and rating come along. The recipe says where it came from.',
      'Ingredients are named the way your household writes them, and every match is shown, so you can keep it, take it back or change it.',
      'Subscribe without leaving Matlist: the payment form opens right in the app.',
      'What’s new in Settings lists every release. The privacy policy is shorter and plainer, and there are short terms of use.',
    ],
  },
  {
    version: '0.20.0',
    date: '2026-10-12',
    notes: [
      'Shopping mode: tap Start shopping, pick the store, and the list follows the way you walk it. Each store learns its order from your trips.',
      'The list is for planning now. The basket lives in shopping mode, and Finish trip asks what the trip cost.',
      'Stats, next to the people at the top: trips per week, spend per month, staples, stores and your recent trips.',
    ],
  },
  {
    version: '0.19.0',
    date: '2026-10-10',
    notes: [
      'Share your trip summary as a picture, with a link that lets friends try Matlist.',
    ],
  },
  {
    version: '0.17.0',
    date: '2026-10-10',
    notes: [
      'Opening a recipe now shows the recipe: ingredients scaled to your servings, marked when they are already on your list, and the method step by step.',
      'Add to list and Cook sit at the foot of the recipe. Cook starts cooking mode right there, no plan needed.',
      'Refresh from link fetches the recipe page again and fills in what is missing, like the method, without touching your own edits.',
    ],
  },
  {
    version: '0.16.0',
    date: '2026-10-10',
    notes: [
      'Write your own recipes, or change any part of one: name, servings, time, ingredients and method. Add a photo too.',
      'Save a week of meals as a template and put it on any week in one tap. One Undo takes it back.',
      'Adding a recipe uses the names your household already has, so "fast potatis" goes on the list as the "potatis fast" you bought before.',
      'Settings › Item history: forget names you no longer buy, so they stop showing up in From before and Running low.',
    ],
  },
  {
    version: '0.15.0',
    date: '2026-10-09',
    notes: [
      'Three tabs: List, Recipes and Plan. Settings now lives behind the avatars at the top.',
      'Recipes: paste a link to import one, then search, filter, tag and star them. Change the servings and every amount follows.',
      'Add one recipe or several to the list in one go. Anything already on it asks first: keep yours, use the recipe, or add both. One Undo takes it all back.',
      'Plan the week: put meals on days, tick the days you shop for, and add what they need.',
      'Cook from the plan: the screen stays on, steps show what they use, and timers keep running while you move on.',
    ],
  },
  {
    version: '0.14.2',
    date: '2026-10-06',
    notes: [
      'Added something by mistake? Every add shows Undo for a few seconds, just like a delete.',
      'Tap the name of a past item to change the amount or category before adding it. The plus adds it straight away.',
    ],
  },
  {
    version: '0.14.1',
    date: '2026-10-06',
    notes: [
      'Ticked items from an earlier trip leave the basket by themselves after 12 hours. They stay one search away when you type.',
      'A long list shows one smooth bar at the top instead of many tiny pills.',
    ],
  },
  {
    version: '0.14.0',
    date: '2026-10-06',
    notes: [
      'Every list starts with a 14-day free trial, no card needed. Lists that were on the free plan get a fresh 14 days.',
      'After that, one Household plan: every feature, for up to six people.',
      'If a trial or plan runs out, the list turns read-only. Everyone still sees it, and nothing is deleted.',
      'Typing an item searches everything your household has added before, not just today’s list. One tap puts it back, in its usual category.',
    ],
  },
  {
    version: '0.13.0',
    date: '2026-10-05',
    notes: [
      'Shopping is a trip now: swipe an item right to drop it in the basket, then Finish trip at the till. Changed your mind? Put it back.',
      'Swipe left to delete, with Undo for five seconds, on every phone at once.',
      '“Running low” chips bring back what you buy regularly, when it’s due.',
      'On an iPad or a computer, Matlist opens a planning view with every category side by side.',
      'Light or dark, your choice: the moon in the header, or Appearance in the sidebar.',
    ],
  },
  {
    version: '0.12.0',
    date: '2026-10-03',
    notes: [
      'Sign-up emails show 15min validation time for sign-up codes.',
    ],
  },
  {
    version: '0.11.0',
    date: '2026-10-03',
    notes: [
      'Sign-up emails actually arrive now — with an 8-digit code you type into the app instead of a link, so it works from the home-screen app too.',
      'Forgot your password? There’s a link for that on the sign-in screen now.',
    ],
  },
  {
    version: '0.10.0',
    date: '2026-10-01',
    notes: [
      'A new look: the Matlist "check m" in lime on ink, with a fresh wordmark — add Matlist to your home screen again to pick up the new icon.',
      'Dark mode is properly dark now.',
    ],
  },
  {
    version: '0.9.0',
    date: '2026-10-01',
    notes: [
      'Listly is now Matlist — same list, same family, new name and a new home at matlist.app. The old address keeps working and takes you there.',
      'A proper icon, finally: add Matlist to your home screen again to pick it up.',
    ],
  },
  {
    version: '0.8.0',
    date: '2026-09-18',
    notes: [
      'Every list includes two people. On Listly Plus, add a seat for each person beyond that from Profile › People — the price is right there, prorated for the rest of your period.',
      'See who is on your list, remove someone, or leave — and hand out a fresh invite code afterwards. "Join another list" works again.',
      'One watch per person: Profile shows when yours was connected, and pairing another replaces it.',
      'Recipe import reads English recipe sites properly — "2 x 400g cans", "500g/1lb 2oz", "juice of 1 lemon", "a pinch of" — and knows that garlic is vitlök: an English recipe merges into the Swedish items already on your list, and vice versa.',
    ],
  },
  {
    version: '0.7.0',
    date: '2026-09-16',
    notes: [
      'Listly is now free to use — no trial, no lock-out. A free list holds up to 5 items at a time; tick things off to make room.',
      'Listly Plus (€19 a year or €2.99 a month) unlocks unlimited items and recipes for everyone on the list. Upgrade from the menu or Profile.',
      'Your Garmin watch keeps syncing on either plan.',
    ],
  },
  {
    version: '0.6.0',
    date: '2026-08-26',
    notes: [
      'Listly is €19 a year for your whole list — or €2.99 a month if you\'d rather. One subscription covers everyone.',
      'Pick your plan right on the subscribe screen, and see the price in Profile while your trial is running.',
    ],
  },
  {
    version: '0.5.0',
    date: '2026-08-26',
    notes: [
      'Every list now starts with a 14-day free trial, then keeps going on a subscription for the whole household — one payment covers everyone on the list.',
      'Manage or cancel it any time from Profile.',
    ],
  },
  {
    version: '0.4.0',
    date: '2026-08-26',
    notes: [
      'GrocerySync is now called Listly — same list, same family, new name.',
      'The sign-in screen doubles as the app\'s front page: tap "What is Listly?" to see what it does.',
    ],
  },
  {
    version: '0.3.1',
    date: '2026-08-23',
    notes: [
      'Saved recipes now remember every ingredient, not just the ones you added to the list.',
      'Ingredients you unticked are kept aside as pantry staples — shown with the recipe, but never counted as missing.',
    ],
  },
  {
    version: '0.3.0',
    date: '2026-08-23',
    notes: [
      'Recipes you import are now saved, so you can cook them again later.',
      'The new Recipes view sorts them by what you can almost cook, and adds only the ingredients you\'re missing.',
      'Categories now sit below each other, with a count of what\'s left in each.',
    ],
  },
  {
    version: '0.2.0',
    date: '2026-08-23',
    notes: [
      'New versions now wait for you — you get a prompt and decide when to update.',
      'After updating, this sheet shows what changed.',
    ],
  },
]

/**
 * The release to announce, or null for nothing.
 *
 * `lastSeen === null` means a first-ever install: record the version silently
 * rather than greeting a new user with release notes.
 */
export function releaseToShow(current: string, lastSeen: string | null): Release | null {
  if (lastSeen === null || lastSeen === current) return null
  return CHANGELOG.find((r) => r.version === current) ?? null
}
